import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Scale, Boxes, Settings as SettingsIcon, Gift, ShieldCheck, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCageDate } from '../contexts/CageDateContext';
import api from '../api';

// ----------------------------------------------------------------
// CageBalancingTabs
// ----------------------------------------------------------------
// Sub-navigation for the Cage Balancing (CBS) compliance suite. This is
// now a top-level area of its own (see the "Cage Balancing" sidebar tab in
// Layout, and the standalone Chip Bank switch), so it carries its own tab
// row separate from the Player Accounts pages. Each tab is permission-gated.
//
// Each data tab also carries a completeness dot for the working day, so an
// unfinished day is visible without opening all four tabs. The four are not on
// the same gaming day - Balance Sheet and Drop Count sit on the anchor, Free
// Play and Chip Liability on the day before it - so the backend is handed the
// anchor once and judges each tab against its own date (cbsService.getDayStatus).
//
// "Complete" means finished, not merely typed: both shifts closed and every
// dropped game metered, the drop committed, the liability signed. A day left
// with an open swing shift reads amber, which is the whole point.

const CAGE_BALANCING = [
  { to: '/cage-balance', label: 'Balance Sheet', icon: Scale, perm: 'cbs:view', key: 'balance' },
  { to: '/cage-drop-count', label: 'Drop Count', icon: Boxes, perm: 'cbs:view', key: 'drop' },
  { to: '/cage-free-play', label: 'Free Play', icon: Gift, perm: 'cbs:liability:enter', key: 'freePlay' },
  { to: '/cage-chip-liability', label: 'Chip Liability', icon: ShieldCheck, perm: 'cbs:liability:enter', key: 'liability' },
  { to: '/cage-reports', label: 'Reports', icon: FileText, perm: 'cbs:view' },
  { to: '/cage-gl-mapping', label: 'GL Mapping', icon: SettingsIcon, perm: 'cbs:configure' },
];

// Colour carries the meaning, but so does the shape: filled = done, ring =
// started, hollow = untouched. A dot that only differed by hue would be a
// coin-flip for the colour-blind, and this row is read at 4am.
const DOT = {
  complete: 'bg-emerald-400 border-emerald-400',
  partial: 'bg-amber-400/20 border-amber-400',
  empty: 'bg-transparent border-gray-600',
};

const fmtShort = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  });
};

export default function CageBalancingTabs() {
  const { can } = useAuth();
  const { anchorDate } = useCageDate(0);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    if (!anchorDate || !can('cbs:view')) return;
    try {
      const res = await api.get('/admin/cbs/day-status', { params: { anchor: anchorDate } });
      setStatus(res.data);
    } catch {
      // Never let a badge failure break navigation - the tabs still work.
      setStatus(null);
    }
  }, [anchorDate, can]);

  useEffect(() => {
    load();
  }, [load]);

  // The dots go stale the moment someone saves on the page they are looking at,
  // so refresh when the tab regains focus rather than leaving a wrong answer up.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const tabs = CAGE_BALANCING.filter((t) => !t.perm || can(t.perm));
  if (tabs.length === 0) return null;

  const anyIncomplete =
    status && Object.values(status.tabs).some((t) => t.state !== 'complete');

  return (
    <div className="-mt-2 mb-4 border-b border-gray-700">
      <div className="flex flex-wrap gap-1">
        {tabs.map((t) => {
          const Icon = t.icon;
          const st = t.key && status ? status.tabs[t.key] : null;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              title={st ? `${fmtShort(st.date)} — ${st.detail}` : undefined}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-amber-500 text-amber-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`
              }
            >
              <Icon size={16} />
              {t.label}
              {st && (
                <span
                  aria-label={`${t.label}: ${st.detail}`}
                  className={`h-2 w-2 shrink-0 rounded-full border ${DOT[st.state] || DOT.empty}`}
                />
              )}
            </NavLink>
          );
        })}
      </div>

      {anyIncomplete && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pb-2 text-[11px] text-gray-500">
          <span className="text-gray-400">Still open for this day:</span>
          {CAGE_BALANCING.filter((t) => t.key && status.tabs[t.key].state !== 'complete').map((t) => {
            const st = status.tabs[t.key];
            return (
              <span key={t.key} className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full border ${DOT[st.state]}`} />
                <span className="text-gray-400">{t.label}</span>
                <span className="text-gray-600">{fmtShort(st.date)}</span>
                <span>· {st.detail}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
